import type { QueryKey } from '@tanstack/react-query';

import { ENDPOINTS, type SearchPackageVersionsResponse } from '../../../../shared/registrySearch';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';

const STALE_MS = 60_000;
const SEARCH_LIMIT = 50;

const getQueryKey = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  query: string,
): QueryKey => ['registry-search', 'versions', fetcher, packageName, query];

/**
 * Fetches the version list for a given package from the registry-search facade. Used to power
 * the version dropdown in the Add Dependency drawer.
 *
 * `query` is an optional refinement substring forwarded to the backend. For fetchers with
 * native version search (e.g. GitHub git-refs) it filters server-side; for fetchers without
 * (e.g. npm) the server fetches the full list once and filters locally. Empty/omitted query
 * returns the latest capped result set.
 *
 * Disabled until `fetcher` and `packageName` are set AND the caller signals via `enabled`.
 * The extra gate lets callers skip the lookup entirely for inputs we structurally can't
 * autocomplete (e.g. a `gitlab.com/...` git locator while only GitHub has a `RegistryServer`).
 */
export const useSearchPackageVersions = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  query: string = '',
  enabled: boolean = true,
) => useQuery<SearchPackageVersionsResponse>({
  queryKey: getQueryKey(fetcher, packageName, query),
  queryFn: () => get<SearchPackageVersionsResponse>(
    ENDPOINTS.SEARCH_PACKAGE_VERSIONS,
    false,
    {
      fetcher: fetcher ?? '',
      package: packageName,
      ...(query ? { q: query } : {}),
      limit: SEARCH_LIMIT,
    },
  ),
  enabled: enabled && !!fetcher && !!packageName,
  staleTime: STALE_MS,
});
